import {Box} from '@mantine/core';
import React, {useState} from 'react';

import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import CreateProteinForm, {
  getCreateFormValidation,
  isSelectedEntityLinkField,
  EntityLinkFields,
  EntityLinkFieldOrDummy,
  UNSELECTED_DUMMY_OBJECT,
} from 'molbio/aa-sequences/CreateAaSequenceForm';
import {BioentityLinkFieldWithTargetSchema} from 'schemas/fields/SchemaFieldConstants';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import {StudySearchItem} from 'studies/StudySearchInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

export interface ConfirmCreateProteinPayload {
  name: string;
  folderId: string;
  entityLinkField?: BioentityLinkFieldWithTargetSchema | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}
export interface CreateProteinModalProps {
  title: string;
  description?: string;
  initialFolderId?: string;
  namePlaceholder?: string;
  onRequestSubmit: (result: ConfirmCreateProteinPayload) => void;
  onRequestClose: () => void;
  entityLinkFields?: EntityLinkFields;
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType>;
  entityLinkFieldLabel?: string;
}

export default function CreateProteinModal({
  title,
  description,
  initialFolderId,
  namePlaceholder,
  onRequestSubmit,
  onRequestClose,
  entityLinkFields = [],
  oligoAwareFolderItemTypes,
  entityLinkFieldLabel,
}: CreateProteinModalProps): JSX.Element {
  const [name, setName] = useState(namePlaceholder ?? '');
  const [folderId, setFolderId] = useState<Optional<string>>(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [tagSchemaId, setTagSchemaId] = useState<string | null>(null);
  const [selectedEntityLinkField, setSelectedEntityLinkField] =
    useState<EntityLinkFieldOrDummy>(UNSELECTED_DUMMY_OBJECT);
  const [selectedStudyItems, setSelectedStudyItems] = useState<Array<StudySearchItem>>([]);

  const {interfaceIdsBySchemaId} = useSchemaSSIsByIdQuery();

  const {isValid} = getCreateFormValidation({
    name,
    folderId,
    selectedEntityLinkField,
    entityLinkFields,
    tagSchemaId,
    interfaceIdsBySchemaId,
  });

  const handleConfirm = (): void => {
    const selectedEntityLinkFieldValue: BioentityLinkFieldWithTargetSchema | null = isSelectedEntityLinkField(
      selectedEntityLinkField
    )
      ? selectedEntityLinkField
      : null;
    const confirmPayload: ConfirmCreateProteinPayload = {
      folderId: folderId!,
      name,
      entityLinkField: selectedEntityLinkFieldValue,
    };
    if (tagSchemaId != null) {
      confirmPayload.tagSchemaId = tagSchemaId;
    }
    if (selectedStudyItems.length > 0) {
      confirmPayload.studyIds = selectedStudyItems.map((studyItem) => studyItem.id);
    }
    onRequestSubmit(confirmPayload);
    onRequestClose();
  };

  const handleFolderIdChange = (newFolderId: string): void => {
    setFolderId(newFolderId);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
  };

  const buttons = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Create',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: !isValid,
      onClick: handleConfirm,
    },
  ];

  return (
    <Modal
      title={title}
      showHeaderBorder={false}
      enableRedesign={true}
      onRequestClose={onRequestClose}
      buttons={buttons}
    >
      {description != null && <Box pb="sm">{description}</Box>}
      <CreateProteinForm
        name={name}
        folderId={folderId}
        tagSchemaId={tagSchemaId}
        selectedEntityLinkField={selectedEntityLinkField}
        selectedStudyItems={selectedStudyItems}
        onNameChange={setName}
        onFolderIdChange={handleFolderIdChange}
        onTagSchemaIdChange={setTagSchemaId}
        onEntityLinkFieldChange={setSelectedEntityLinkField}
        onSelectedStudyItemsChange={setSelectedStudyItems}
        entityLinkFields={entityLinkFields}
        oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
        entityLinkFieldLabel={entityLinkFieldLabel}
      />
    </Modal>
  );
}
